<script setup lang="ts">
import { computed } from 'vue';
import { customListStatusFor } from './customListStatus';

/**
 * A custom list's status, as one icon with the status's name as its tooltip.
 *
 * Every status draws an icon, so a heading always says which one the list is
 * in ({@link customListStatusFor} picks it). A row with no status, or one the
 * status list does not know, draws nothing.
 *
 * It sits inside a heading. A screen reader reads it as "(Disabled)" after the
 * name, the same words the heading's tooltip ends the name with, rather than
 * running the status into the name.
 */
const props = defineProps<{
    statusId: unknown;
}>();

const status = computed(() => customListStatusFor(props.statusId));
</script>

<template>
    <span
        v-if="status"
        class="inline-flex shrink-0 items-center"
        :class="status.tone"
        :title="status.label"
        :data-status-id="status.id"
        data-testid="custom-list-status-icon"
    >
        <component :is="status.icon" class="size-[1em]" aria-hidden="true" />
        <span class="sr-only">({{ status.label }})</span>
    </span>
</template>
